// Time entries (screen 4): the seat's live entries for the focused week from
// `GET /time-entries?seat=&period=` (one call). Founder time is agency-visibility, so only
// agency operators ever receive those rows; the table shows whatever the API returns. Managers
// get row actions (edit, approve or reject, void: time-entry-actions.tsx) and "Enter hours".
import {
  Badge,
  buttonVariants,
  cn,
  EmptyState,
  Segmented,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ClockIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile, TimeEntry } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { weekRangeLabel } from "../../focus/weeks";
import { type CompanyAccess, isManager } from "../company";
import { formatDate, formatMinutes, TIME_KIND_LABELS } from "../format";
import { timeEntriesQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { TimeEntryActions } from "./time-entry-actions";

export const APPROVAL = {
  approved: { label: "Approved", tone: "success" },
  pending: { label: "Pending", tone: "warning" },
  rejected: { label: "Rejected", tone: "danger" },
} as const satisfies Record<TimeEntry["approval_status"], { label: string; tone: string }>;

export function ApprovalBadge({ entry }: { entry: TimeEntry }) {
  const a = APPROVAL[entry.approval_status];
  return (
    <Badge
      variant={a.tone}
      title={entry.rejection_reason ?? undefined}
      data-approval={entry.approval_status}
    >
      {a.label}
    </Badge>
  );
}

export function TimeTab({
  api,
  company,
  seatId,
  week,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  seatId: string;
  week: string;
  de: DeProfile;
}) {
  const manager = isManager(company);
  const [filter, setFilter] = useState<"all" | "pending">("all");
  const entries = useInfiniteQuery(timeEntriesQuery(api, company.id, seatId, week));
  if (entries.isPending) return <Loading label="Loading time entries" rows={4} />;
  if (entries.isError) return <LoadError what="Time entries" error={entries.error} />;
  const all = entries.data.pages.flatMap((p) => p.data);
  const pending = all.filter((r) => r.approval_status === "pending").length;
  const rows = filter === "pending" ? all.filter((r) => r.approval_status === "pending") : all;
  // With more pages on the server, the totals cover the loaded rows only, and say so.
  const partial = entries.hasNextPage;
  const enter = manager ? (
    <Link
      to="/c/$slug/kpis/entry/hours"
      params={{ slug: company.slug }}
      search={keepFocus}
      className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
    >
      Enter hours
    </Link>
  ) : null;
  if (!all.length)
    return (
      <EmptyState
        variant="card"
        icon={ClockIcon}
        title={`No time entries in ${week}`}
        description={`Hours logged for ${de.display_name}'s seat appear here.`}
        actions={enter}
      />
    );
  const totals = new Map<string, number>();
  for (const r of rows) totals.set(r.kind, (totals.get(r.kind) ?? 0) + r.minutes);
  // The entry carries a membership id only. The supervisor's name comes with the profile;
  // anyone else stays a neutral, unresolved member (no guessing who they are).
  const supervisor = de.supervisor;
  const person = (membershipId: string) =>
    supervisor?.membership_id === membershipId
      ? (supervisor.name ?? "Supervisor")
      : `Member …${membershipId.slice(-4)}`;
  const span = manager ? 3 : 2;
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Segmented
          aria-label="Show entries"
          size="sm"
          value={filter}
          onValueChange={setFilter}
          options={[
            { value: "all", label: `All (${all.length})` },
            { value: "pending", label: `Needs approval (${pending})` },
          ]}
        />
        {enter}
      </div>
      <Table>
        <TableCaption>
          Time entries for week {week} ({weekRangeLabel(week)}).
        </TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Date</TableHead>
            <TableHead scope="col">Kind</TableHead>
            <TableHead scope="col">Person</TableHead>
            <TableHead scope="col" className="text-right">
              Duration
            </TableHead>
            <TableHead scope="col">Approval</TableHead>
            <TableHead scope="col">Note</TableHead>
            {manager ? (
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.map((r) => (
              <TableRow key={r.id} className="group/row" data-kind={r.kind} data-entry={r.id}>
                <TableCell>{formatDate(r.work_date)}</TableCell>
                <TableHead scope="row" className="font-normal">
                  {TIME_KIND_LABELS[r.kind] ?? r.kind}
                  {r.visibility === "agency" ? (
                    <Badge variant="secondary" className="ml-1.5">
                      Agency
                    </Badge>
                  ) : null}
                </TableHead>
                <TableCell title={r.membership_id}>{person(r.membership_id)}</TableCell>
                <TableCell className="text-right tabular-nums" data-field="minutes">
                  {formatMinutes(r.minutes)}
                </TableCell>
                <TableCell>
                  <ApprovalBadge entry={r} />
                </TableCell>
                <TableCell className="max-w-64 truncate text-muted-foreground" title={r.note ?? ""}>
                  {r.note ?? "—"}
                </TableCell>
                {manager ? (
                  <TableCell className="text-right">
                    <TimeEntryActions
                      api={api}
                      companyId={company.id}
                      entry={r}
                      who={person(r.membership_id)}
                    />
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={manager ? 7 : 6}>
                <EmptyState variant="compact" title="Nothing needs approval" />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          {[...totals].map(([kind, minutes]) => (
            <TableRow key={kind}>
              <TableHead scope="row" colSpan={3}>
                Total {TIME_KIND_LABELS[kind]?.toLowerCase() ?? kind}
                {partial ? " (loaded entries only)" : ""}
              </TableHead>
              <TableCell className="text-right tabular-nums" data-total={kind}>
                {formatMinutes(minutes)}
              </TableCell>
              <TableCell colSpan={span} />
            </TableRow>
          ))}
        </TableFooter>
      </Table>
      <LoadMore
        what="time entries"
        hasMore={entries.hasNextPage}
        loading={entries.isFetchingNextPage}
        onMore={() => void entries.fetchNextPage()}
      />
    </div>
  );
}
